import { Workbook } from 'exceljs';
import { saveAs } from 'file-saver';
import { getDataBase64 } from '../lib/Util';
import { APP_NAME } from './constants';

export interface ExcelColumn<T = any> {
  key: keyof T | string;
  header: string;
  width?: number;
  formatter?: (value: any, row: T) => any;
}

export interface ExportArrayToExcelOptions<T = any> {
  fileName: string;
  sheetName?: string;
  title?: string;
  data: T[];
  columns: ExcelColumn<T>[];
  extraInfoRows?: { label: string; value: string }[];
  headerBackgroundColor?: string;
  headerFontColor?: string;
}

export async function exportArrayToExcel<T>({
  fileName,
  sheetName = 'Main Sheet',
  title,
  data,
  columns,
  extraInfoRows = [],
  headerBackgroundColor,
  headerFontColor,
}: ExportArrayToExcelOptions<T>) {
  const workbook = new Workbook();
  const worksheet = workbook.addWorksheet(sheetName);

  const imageId = workbook.addImage({
    base64: getDataBase64(),
    extension: 'png',
  });

  worksheet.addImage(imageId, {
    tl: { col: 0, row: 0 },
    ext: { width: 400, height: 100 },
  });

  worksheet.getRow(1).height = 80;

  worksheet.getCell('A2').value = APP_NAME;

  worksheet.mergeCells(3, 1, 3, columns.length);

  const titleCell = worksheet.getCell('A3');
  titleCell.value = title ?? '';
  titleCell.font = {
    name: 'Segoe UI Light',
    size: 22,
  };
  titleCell.alignment = {
    horizontal: 'center',
  };

  let currentRow = 4;

  extraInfoRows.forEach((info) => {
    worksheet.getCell(currentRow, 1).value = info.label;
    worksheet.getCell(currentRow, 1).font = { bold: true };

    worksheet.getCell(currentRow, 2).value = info.value;

    currentRow++;
  });

  const headerRowNumber = currentRow;

  const headerRow = worksheet.getRow(headerRowNumber);

  columns.forEach((column, index) => {
    const cell = headerRow.getCell(index + 1);

    cell.value = column.header;

    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: {
        argb: headerBackgroundColor ?? 'FF4472C4',
      },
    };

    cell.font = {
      bold: true,
      color: {
        argb: headerFontColor ?? 'FFFFFFFF',
      },
    };

    worksheet.getColumn(index + 1).width = column.width ?? Math.max(column.header.length + 5, 20);
  });

  currentRow++;

  data.forEach((row) => {
    columns.forEach((column, columnIndex) => {
      const rawValue = (row as any)[column.key];

      const value = column.formatter ? column.formatter(rawValue, row) : rawValue;

      worksheet.getCell(currentRow, columnIndex + 1).value = value;
    });

    currentRow++;
  });

  const buffer = await workbook.xlsx.writeBuffer();

  saveAs(
    new Blob([buffer], {
      type: 'application/octet-stream',
    }),
    `${fileName}.xlsx`,
  );
}
